import { Button } from '@hologram-dimension/button';
import { ComplexIcon, Loader } from 'holokit';
import { LEARN_SEND_LOG } from 'common-js/constants/links';
import useAppDispatch from 'common-js/hooks/useAppDispatch';
import useAppSelector from 'common-js/hooks/useAppSelector';
import { HEARTBEAT, MODIFIER, OUTBOUND } from 'common-js/reducers/console/models';
import { selectConsoleMessages } from 'common-js/reducers/console/selectors';
import { selectViewFilters } from 'common-js/reducers/drawer/selectors';
import { getLogs, retryLoad, revealLogs, unmountLogs } from 'common-js/reducers/log/actions';
import {
  selectDeviceFilter,
  selectIsViewingAllLogs,
  selectSearchQuery,
  selectSelectedTags,
} from 'common-js/reducers/logFilter/selectors';
import { type FC, useEffect, useMemo } from 'react';
import HeartbeatMessage from '../components/HeartbeatMessage';
import ModifierMessage from '../components/ModifierMessage';
import OutboundMessage from '../components/OutboundMessage';

interface MessageProps {
  message: any;
}

const Message: FC<MessageProps> = ({ message }) => {
  switch (message.type) {
    case OUTBOUND:
      return <OutboundMessage message={message} />;
    case HEARTBEAT:
      return <HeartbeatMessage message={message} />;
    case MODIFIER:
      return <ModifierMessage message={message} />;
    default:
      return null;
  }
};

const logsIcon = (
  <svg className="Console__empty-icon" viewBox="0 0 25 20">
    <g fill="none" fillRule="evenodd" strokeLinecap="round" strokeLinejoin="round">
      <path d="M24 17c0 1.104-.894 2-2 2H3c-1.104 0-2-.896-2-2V3c0-1.104.896-2 2-2h19c1.106 0 2 .896 2 2v14zM2 6h22" />
      <path d="M5 3.5c0 .276-.224.5-.5.5S4 3.776 4 3.5s.224-.5.5-.5.5.224.5.5zM8 3.5c0 .276-.224.5-.5.5S7 3.776 7 3.5s.224-.5.5-.5.5.224.5.5zM11 3.5c0 .276-.224.5-.5.5s-.5-.224-.5-.5.224-.5.5-.5.5.224.5.5zM1 13h5l2-3 2 5 4-6 3 7 2-3h5" />
    </g>
  </svg>
);

interface ConsoleProps {
  pollLogData: boolean;
}

const Console: FC<ConsoleProps> = ({ pollLogData }) => {
  const dispatch = useAppDispatch();
  const viewFilters = useAppSelector(selectViewFilters);
  const deviceFilter = useAppSelector(selectDeviceFilter);
  const searchQuery = useAppSelector(selectSearchQuery);
  const isViewingAllLogs = useAppSelector(selectIsViewingAllLogs);
  const selectedTags = useAppSelector(selectSelectedTags);

  const isLoadingLogs = useAppSelector((state) => state.log.isLoadingLogs);
  const isContinuing = useAppSelector((state) => state.log.isContinuing);
  const isUpdating = useAppSelector((state) => state.log.isUpdating);
  const hasMoreLogs = useAppSelector((state) => state.log.hasMoreLogs);
  const wasError = useAppSelector((state) => state.log.wasError);

  const messages = useAppSelector(selectConsoleMessages);

  const showFilters = !!(
    viewFilters ||
    selectedTags?.length > 0 ||
    !isViewingAllLogs ||
    searchQuery?.length > 0 ||
    deviceFilter
  );

  const isLoading = isLoadingLogs && !isUpdating && !isContinuing;

  const { filteredMessages, unRevealedMessagesCount } = useMemo(() => {
    const newFilteredMessages = new Map();
    const newUnRevealedMessagesCount = messages.reduce((count, message) => {
      let newCount = count;
      if (message.unRevealed) {
        newCount += 1;
      } else {
        newFilteredMessages.set(message.logId, message);
      }
      return newCount;
    }, 0);

    return {
      filteredMessages: [...newFilteredMessages.values()],
      unRevealedMessagesCount: newUnRevealedMessagesCount,
    };
  }, [messages]);

  useEffect(() => {
    // TODO: Just use useSWR?
    if (pollLogData) {
      const refreshIntervalHandle = window.setInterval(() => {
        if (!isLoadingLogs && !wasError) {
          dispatch(getLogs(false, true));
        }
      }, 10000);

      return () => {
        window.clearInterval(refreshIntervalHandle);
      };
    }

    return undefined;
  }, [dispatch, isLoadingLogs, pollLogData, wasError]);

  useEffect(
    () => () => {
      dispatch(unmountLogs());
    },
    [dispatch]
  );

  return (
    <div className={`Console${showFilters ? ' show-filters' : ''}`}>
      <Loader isLoading={isLoading} />
      {!isLoading && (
        <div className="full-height">
          {wasError && (
            <div className="empty-state">
              <div className="section">
                {logsIcon}
                <h2 className="no-margin">An error occurred when trying to load messages</h2>
                <div className="f2">
                  Please try reloading the messages or apply different filters
                </div>
              </div>
              <Button
                onClick={() => {
                  dispatch(retryLoad);
                }}
                variant="secondary"
              >
                Reload
              </Button>
            </div>
          )}
          {!wasError &&
            (messages.length > 0 ? (
              <div>
                {unRevealedMessagesCount > 0 && (
                  <div className="grid-row h-align v-align load-more-panel">
                    <Button
                      onClick={() => {
                        dispatch(revealLogs());
                      }}
                      variant="secondary"
                    >
                      {`View ${unRevealedMessagesCount} new messages`}
                    </Button>
                  </div>
                )}
                {filteredMessages.map((message) => (
                  <Message key={`consoledisplaymessage${message.logId}`} message={message} />
                ))}
                {isContinuing && (
                  <div className="grid-row h-align v-align loading-more-panel">
                    <ComplexIcon name="spinner" />
                  </div>
                )}
                {!isContinuing && hasMoreLogs && (
                  <div className="grid-row h-align v-align loading-more-panel" />
                )}
                {!isContinuing && !hasMoreLogs && (
                  <div className="grid-row h-align v-align loading-more-panel">
                    <div className="f6">All messages loaded for this filter.</div>
                  </div>
                )}
              </div>
            ) : (
              <div className="empty-state">
                <div className="section">
                  {logsIcon}
                  <h2 className="no-margin">
                    No messages with these filters have been logged yet.
                  </h2>
                  <div className="f2">
                    Data will be logged whenever a message from your device is sent to the cloud.
                  </div>
                </div>
                <a className="a-underline" rel="noreferrer" target="_blank" href={LEARN_SEND_LOG}>
                  Learn how to send messages to the cloud
                </a>
              </div>
            ))}
        </div>
      )}
    </div>
  );
};

export default Console;
